/**
 * What the reader was reading when they tapped "Start your 25 days free" (owner, 2026-10-13: "more conversion (deeper conversion)";
 * docs/marketing/CONVERSION-2026-10-13.md fix C2). A library guide or a blog article adds `read=<kind>:<id>` to its sign-up links:
 *
 *   read=lib:sick-workers            a library guide (core LIBRARY id)
 *   read=blog:coffee-shop-opening…   a Utah blog article (its slug)
 *   read=blog-nv:<slug>, blog-ca:…   another state's blog article
 *
 * The app's sign-up screen then says "You were reading: <title>". The link carries ONLY an id: the title always comes from our own
 * lists (core LIBRARY, generated/blog-titles.ts), so a crafted link can never put its own words on the sign-up screen. An unknown id,
 * a malformed value or a guide that isn't Utah's library shows nothing (and is dropped, never stored).
 */
import { LIBRARY } from "./library.ts";
import { BLOG_TITLES } from "./generated/blog-titles.ts";

export const READ_PARAM = "read";
export type ReadKind = "lib" | "blog" | "blog-nv" | "blog-ca";
export interface ReadFrom { kind: ReadKind; id: string }

const READ_RE = /^(lib|blog|blog-nv|blog-ca):([a-z0-9][a-z0-9-]{0,99})$/;
const BLOG_STATE: Record<Exclude<ReadKind, "lib">, string> = { blog: "UT", "blog-nv": "NV", "blog-ca": "CA" };

/** The page's link value ("lib:sick-workers"), for the site's buttons. */
export const readValue = (r: ReadFrom): string => `${r.kind}:${r.id}`;

/** The title we know for it, or null (an id we have never published or drafted). */
export function readTitle(r: ReadFrom): string | null {
  if (r.kind === "lib") return LIBRARY.find((t) => t.id === r.id)?.title ?? null;
  return BLOG_TITLES[`${BLOG_STATE[r.kind]}:${r.id}`] ?? null;
}

/** The value as a ReadFrom, only when it is well formed AND names something we know; anything else is null. */
export function readFromParam(v: string | null | undefined): ReadFrom | null {
  const m = String(v ?? "").trim().match(READ_RE);
  if (!m) return null;
  const r: ReadFrom = { kind: m[1] as ReadKind, id: m[2]! };
  return readTitle(r) ? r : null;
}

/** The address's `read` (…/app/?signup=1&read=lib:sick-workers), or null. */
export function readFromQuery(search: string | URLSearchParams): ReadFrom | null {
  return readFromParam((typeof search === "string" ? new URLSearchParams(search.startsWith("?") ? search.slice(1) : search) : search).get(READ_PARAM));
}

/**
 * The sign-up screen's words. Only what is true on every path: the guide or article stays free on the site, and a library guide is
 * also in the app (Learn → Library, the same core text). Nothing about what setup shows next: the buyer path and the plain path differ.
 */
export function readWords(r: ReadFrom): { kicker: string; title: string; note: string } | null {
  const title = readTitle(r);
  if (!title) return null;
  return {
    kicker: "You were reading",
    title,
    note: r.kind === "lib"
      ? "It stays free on our site, and it's in the app too, under Learn, then Library."
      : "It stays free to read on our site.",
  };
}
